import { useEffect, useState } from 'react';
import { Check, ChevronsUpDown, Globe, KeyRound, LogOut } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/useAuth';
import { FRONTEND_VERSION, FRONTEND_BUILD } from '@/config/version';
import { AVAILABLE_LANGUAGES, getLanguage, setLanguage, t } from '@/i18n';
import { initialsFromName } from '@/lib/hr';
import { ApiTokenDialog } from './ApiTokenDialog';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface BackendVersion {
  version: string;
  build: string;
}

export function UserMenu() {
  const { userInfo, isAdmin, logout } = useAuth();
  const [backendVersion, setBackendVersion] = useState<BackendVersion | null>(null);
  const [apiTokenOpen, setApiTokenOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/health/version`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setBackendVersion(data);
      })
      .catch(() => {
        if (!cancelled) setBackendVersion(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent">
          <Avatar className="size-8 rounded-lg">
            <AvatarFallback className="rounded-lg">
              {initialsFromName(userInfo?.name)}
            </AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-medium">{userInfo?.name}</span>
            <span className="text-muted-foreground truncate text-xs">
              {userInfo?.email}
            </span>
          </div>
          <ChevronsUpDown className="ml-auto size-4" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-64">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span>{userInfo?.name}</span>
            <span className="text-muted-foreground text-xs font-normal">
              {userInfo?.email}
            </span>
            {isAdmin && (
              <span className="text-xs font-normal text-emerald-600 dark:text-emerald-400">
                Administrator
              </span>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Globe className="mr-2 size-4" />
            {AVAILABLE_LANGUAGES.find((l) => l.code === getLanguage())?.label}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {AVAILABLE_LANGUAGES.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                disabled={lang.code === getLanguage()}
                onClick={() => {
                  setLanguage(lang.code);
                  window.location.reload();
                }}
              >
                {lang.code === getLanguage() && <Check className="mr-2 size-4" />}
                {lang.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem onClick={() => setApiTokenOpen(true)}>
          <KeyRound className="mr-2 size-4" />
          {t('profile.apiToken.menuItem')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={logout}>
          <LogOut className="mr-2 size-4" />
          Logout
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="text-muted-foreground px-2 py-1.5 font-mono text-[11px] leading-relaxed">
          <div>
            Frontend: v{FRONTEND_VERSION} ({FRONTEND_BUILD})
          </div>
          <div>
            Backend:{' '}
            {backendVersion
              ? `v${backendVersion.version} (${backendVersion.build})`
              : '…'}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
    <ApiTokenDialog open={apiTokenOpen} onOpenChange={setApiTokenOpen} />
    </>
  );
}
